import React, { useMemo, useState } from 'react';
import cn from 'classnames';
import {
  AdminSettingsSection,
  FeatureUnavailableBanner,
  Loader,
  PageBody,
  PageTitle,
} from 'src/components';
import { useFeatureAvailability } from 'src/hooks/useFeatureAvailability';
import {
  useAiCreditLedgerQuery,
  useAiBudgetQuery,
  useAiUsageBreakdownQuery,
} from 'src/api/queries/aiSettingsQueries';
import {
  AiCreditLedgerEntry,
  AiUsageBreakdownUser,
  AiUsageCreditUser,
} from 'src/types/TAiSettings';
import { useAtomValue } from 'jotai/utils';
import { addonsSettingsAtom } from 'src/shared/settings/addons';
import { isErrorResponseResult } from 'src/api/utils/errorHandlers';
import { FetchError } from 'src/types/TApiQueries';
import { formatNumberIntl } from 'src/utils/common';
import { OPERATION_LABELS, ledgerEntryLabel } from './ledgerLabels';
import styles from './styles.module.scss';


const formatDateTime = (raw: string | null): string => {
  if (!raw) return '—';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatDate = (raw: string | null): string => {
  if (!raw) return '—';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
};

const entryBadgeClass = (entryType: AiCreditLedgerEntry['entry_type']): string => {
  if (entryType.startsWith('grant_')) return styles.entryTypeGrant;
  if (entryType === 'debit' || entryType === 'refund') return styles.entryTypeDebit;
  if (entryType === 'expiry') return styles.entryTypeExpiry;
  if (entryType === 'adjustment') return styles.entryTypeAdjustment;
  return '';
};

const entryBadgeLabel = (entryType: AiCreditLedgerEntry['entry_type']): string =>
  entryType.replace(/_/g, ' ');


type ExpiryState = { label: string; className?: string };

const expiryState = (raw: string | null): ExpiryState => {
  if (!raw) return { label: '—' };
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return { label: raw };
  const now = Date.now();
  if (d.getTime() <= now) {
    return { label: formatDate(raw), className: styles.expired };
  }
  const diffDays = Math.ceil((d.getTime() - now) / (1000 * 60 * 60 * 24));
  if (diffDays <= 14) {
    return {
      label: `${formatDate(raw)} (in ${diffDays}d)`,
      className: styles.expiringSoon,
    };
  }
  return { label: formatDate(raw) };
};

// Translate a react-query FetchError into a user-friendly banner for
// the AI Usage page. The backend distinguishes 403 ("forbidden",
// "no_subscription") from 5xx; we surface those cases precisely
// instead of the old generic "addon may not be enabled" copy.
const friendlyErrorBanner = (error: FetchError): string => {
  if (isErrorResponseResult(error)) {
    if (error.status === 403) {
      const code = (error as any).error;
      if (code === 'no_subscription') {
        return 'This account has no credit balance yet. Contact support to enable AI Agent for this subscription.';
      }
      return 'You do not have permission to view credits usage for this account.';
    }
    if (error.status === 404) {
      return 'Credits usage data is not available for this account.';
    }
  }
  return 'Failed to load credits usage. Please try again in a moment.';
};

const formatUserName = (user: {
  first_name?: string;
  last_name?: string;
  email?: string;
  user_id: number;
}): string => {
  const name = `${user.first_name || ''} ${user.last_name || ''}`.trim();
  if (name) return name;
  if (user.email) return user.email;
  return `User #${user.user_id}`;
};

export const AiUsage: React.FC = () => {
  // `selectedGroupId` is `undefined` until the user picks a different
  // group from the dropdown — undefined means "default to caller's
  // own group" on the backend, which is the right behaviour for the
  // first page load.
  const [selectedGroupId, setSelectedGroupId] = useState<number | undefined>(
    undefined,
  );

  const featureStatus = useFeatureAvailability('ai-usage');
  const addonsSettings = useAtomValue(addonsSettingsAtom);
  const aiAddonEnabled = Boolean(
    addonsSettings?.AI_ASSISTANT?.added && addonsSettings?.AI_ASSISTANT?.enabled,
  );

  const {
    data: breakdown,
    isLoading: breakdownLoading,
    error: breakdownError,
  } = useAiUsageBreakdownQuery({ groupId: selectedGroupId, period: 720 });

  const {
    data: ledger,
    isLoading: ledgerLoading,
    error: ledgerError,
  } = useAiCreditLedgerQuery({ limit: 50, groupId: selectedGroupId });

  const { data: budget } = useAiBudgetQuery();

  const isLoading = breakdownLoading || ledgerLoading;
  // We treat either query failing as a hard failure for the page —
  // both are essential to the view and one returning 403 means the
  // user has no business seeing the other either.
  const error = breakdownError || ledgerError;

  const balance = ledger?.balance ?? 0;
  const balanceState = useMemo<'normal' | 'low' | 'exhausted'>(() => {
    if (balance <= 0) return 'exhausted';
    if (balance > 0 && balance < 100) return 'low';
    return 'normal';
  }, [balance]);

  const dailyCap = budget?.credits.daily.limit ?? 0;
  const weeklyCap = budget?.credits.weekly.limit ?? 0;
  const monthlyCap = budget?.credits.monthly.limit ?? 0;

  // AI Usage is a built-in feature: it's always shown. When the account
  // doesn't have the AI addon, or the AI stats endpoints 404 on the old
  // release, render all sections in a read-only/empty state with a "Not
  // enabled" banner so the user gets a sense of the feature; it fills in
  // once the addon is enabled and the API answers.
  const errorStatus = error && isErrorResponseResult(error) ? error.status : undefined;
  const unavailable = !aiAddonEnabled || featureStatus === 'unavailable' || errorStatus === 404;

  if (isLoading) {
    return (
      <div className={styles.pageWrapper}>
        <PageTitle title="Credits Usage" className={styles.pageTitle} />
        <PageBody>
          <div className={styles.loadingContainer}>
            <Loader />
          </div>
        </PageBody>
      </div>
    );
  }

  // A genuine error (e.g. 403 no-permission / no_subscription) is still a hard
  // failure — but a missing backend (unavailable) falls through to the
  // read-only sections below.
  if (!unavailable && (error || !ledger || !breakdown)) {
    const banner = error
      ? friendlyErrorBanner(error)
      : 'Failed to load credits usage data.';
    return (
      <div className={styles.pageWrapper}>
        <PageTitle title="Credits Usage" className={styles.pageTitle} />
        <PageBody>
          <div className={styles.errorBanner}>{banner}</div>
        </PageBody>
      </div>
    );
  }

  // Hidden for now — see TODO.md ("reveal AI operation cost table"). Restore
  // alongside the "What each AI operation costs" section below.
  // const rates = ledger?.rates ?? [];
  // Null-safe: when the feature is unavailable the queries 404'd, so we render
  // the sections with empty data + the "enabling soon" banner.
  const entries = ledger?.entries ?? [];
  const groups = breakdown?.groups ?? [];
  const users = breakdown?.users ?? [];
  const imageEditUsers: AiUsageCreditUser[] = breakdown?.image_edit_users ?? [];

  // Sort defensively client-side — the backend already orders by
  // credits_used desc but a custom backend response or tests might
  // not, and the UI promise is "sorted by credits used".
  const sortedUsers = [...users].sort((a, b) => b.credits_used - a.credits_used);
  const sortedImageEditUsers = [...imageEditUsers].sort(
    (a, b) => b.credits_used - a.credits_used,
  );

  const selectedGroup = groups.find(
    (g) => g.group_id === breakdown?.selected_group_id,
  );

  return (
    <div className={styles.pageWrapper}>
      <PageTitle title="Credits Usage" className={styles.pageTitle} />

      <PageBody>
        {unavailable && <FeatureUnavailableBanner />}

        <div className={styles.groupSelectorRow}>
          <span className={styles.groupSelectorLabel}>Group</span>
          {groups.length > 1 ? (
            <select
              className={styles.groupSelect}
              value={String(breakdown?.selected_group_id ?? '')}
              onChange={(e) => setSelectedGroupId(Number(e.target.value))}
              disabled={unavailable}
              data-test="ai-usage-group-select"
            >
              {groups.map((g) => (
                <option key={g.group_id} value={g.group_id}>
                  {g.name || `Group ${g.group_id}`}
                  {g.is_caller_group ? ' (your group)' : ''}
                </option>
              ))}
            </select>
          ) : (
            <span className={styles.groupBadge}>
              {selectedGroup?.name ||
                (breakdown ? `Group ${breakdown.selected_group_id}` : 'Your group')}
            </span>
          )}
        </div>

        <AdminSettingsSection title="Current Balance">
          <p className={styles.sectionDescription}>
            Live credit balance for this account. Credits are debited per AI operation and per print export beyond the plan allowance.
          </p>

          <div
            className={cn({
              [styles.balanceLow]: balanceState === 'low',
              [styles.balanceExhausted]: balanceState === 'exhausted',
            })}
          >
            <div className={styles.balanceRow}>
              <span className={styles.balanceValue}>{formatNumberIntl(balance)}</span>
              <span className={styles.balanceUnit}>credits remaining</span>
            </div>
          </div>

          {(dailyCap > 0 || weeklyCap > 0 || monthlyCap > 0) && (
            <div className={styles.balanceMeta}>
              Velocity caps in effect:{' '}
              {dailyCap > 0 && <>daily <strong>{formatNumberIntl(dailyCap)}</strong> · </>}
              {weeklyCap > 0 && <>weekly <strong>{formatNumberIntl(weeklyCap)}</strong> · </>}
              {monthlyCap > 0 && <>monthly <strong>{formatNumberIntl(monthlyCap)}</strong></>}
              .{' '}
              Caps are configured on the AI Agent settings page and limit how fast credits can be spent —
              they do not change the balance shown above.
            </div>
          )}
        </AdminSettingsSection>

        <AdminSettingsSection title="AI chat usage by user">
          <p className={styles.sectionDescription}>
            Per-user breakdown of AI chat activity over the last 30 days for the selected group.
          </p>

          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>User</th>
                  <th className={styles.numericCell}>Credits used</th>
                  <th className={styles.numericCell}>Requests</th>
                  <th className={styles.numericCell}>Input tokens</th>
                  <th className={styles.numericCell}>Output tokens</th>
                  <th className={styles.numericCell}>Tool calls</th>
                </tr>
              </thead>
              <tbody>
                {sortedUsers.length === 0 ? (
                  <tr>
                    <td colSpan={6} className={styles.empty}>
                      No chat activity recorded in the last 30 days.
                    </td>
                  </tr>
                ) : (
                  sortedUsers.map((u) => (
                    <tr key={u.user_id}>
                      <td>
                        <div className={styles.userNameCell}>
                          <span className={styles.userNamePrimary}>
                            {formatUserName(u)}
                          </span>
                          {u.email && (
                            <span className={styles.userNameSecondary}>{u.email}</span>
                          )}
                        </div>
                      </td>
                      <td className={cn(styles.numericCell, styles.amountNegative)}>
                        {formatNumberIntl(u.credits_used)}
                      </td>
                      <td className={styles.numericCell}>
                        {formatNumberIntl(u.requests)}
                      </td>
                      <td className={styles.numericCell}>
                        {formatNumberIntl(u.input_tokens)}
                      </td>
                      <td className={styles.numericCell}>
                        {formatNumberIntl(u.output_tokens)}
                      </td>
                      <td className={styles.numericCell}>
                        {formatNumberIntl(u.tool_calls)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {breakdown?.note && (
            <p className={styles.sectionNote}>{breakdown.note}</p>
          )}
        </AdminSettingsSection>

        <AdminSettingsSection title="AI Image Edit usage by user">
          <p className={styles.sectionDescription}>
            Per-user AI Image Edit credit spend over the last 30 days for the selected group.
            Counts every charged edit (enhancement, background removal, generative fill/expand,
            text-to-image) attributed to each user.
          </p>

          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>User</th>
                  <th className={styles.numericCell}>Credits used</th>
                  <th className={styles.numericCell}>Operations</th>
                </tr>
              </thead>
              <tbody>
                {sortedImageEditUsers.length === 0 ? (
                  <tr>
                    <td colSpan={3} className={styles.empty}>
                      No AI Image Edit activity recorded in the last 30 days.
                    </td>
                  </tr>
                ) : (
                  sortedImageEditUsers.map((u) => (
                    <tr key={u.user_id}>
                      <td>
                        <div className={styles.userNameCell}>
                          <span className={styles.userNamePrimary}>
                            {formatUserName(u)}
                          </span>
                          {u.email && (
                            <span className={styles.userNameSecondary}>{u.email}</span>
                          )}
                        </div>
                      </td>
                      <td className={cn(styles.numericCell, styles.amountNegative)}>
                        {formatNumberIntl(u.credits_used)}
                      </td>
                      <td className={styles.numericCell}>
                        {formatNumberIntl(u.operations)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </AdminSettingsSection>

        {/*
          Hidden for now — see TODO.md ("reveal AI operation cost table").
          To restore: uncomment this section and the `rates` line above.

        <AdminSettingsSection
          title="What each AI operation costs"
          collapsible
          defaultCollapsed
        >
          <p className={styles.sectionDescription}>
            Per-operation credit cost. Token-based operations (chat, agent steps with reasoning)
            are billed by token consumption; fixed-cost operations consume the listed flat rate per call.
          </p>

          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Operation</th>
                  <th className={styles.numericCell}>Cost</th>
                </tr>
              </thead>
              <tbody>
                {rates.length === 0 ? (
                  <tr>
                    <td colSpan={2} className={styles.empty}>
                      No rate configuration available.
                    </td>
                  </tr>
                ) : (
                  rates.map((rate) => {
                    const label = OPERATION_LABELS[rate.operation] ?? rate.operation;
                    const cost =
                      rate.credits > 0
                        ? `${formatNumberIntl(rate.credits)} credit${rate.credits === 1 ? '' : 's'} per call`
                        : 'token-based';
                    return (
                      <tr key={rate.operation}>
                        <td>{label}</td>
                        <td className={styles.numericCell}>{cost}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </AdminSettingsSection>
        */}

        <AdminSettingsSection title="Account ledger">
          <p className={styles.sectionDescription}>
            Last 50 grants, debits, expiries and adjustments against this account&apos;s credit pool.
            Includes every operation across all users in the selected group.
          </p>

          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>When</th>
                  <th>Type</th>
                  <th className={styles.numericCell}>Amount</th>
                  <th className={styles.numericCell}>Balance</th>
                  <th>Source</th>
                  <th>Expires</th>
                </tr>
              </thead>
              <tbody>
                {entries.length === 0 ? (
                  <tr>
                    <td colSpan={6} className={styles.empty}>
                      No activity yet.
                    </td>
                  </tr>
                ) : (
                  entries.map((entry) => {
                    const exp = expiryState(entry.expires_at);
                    const amountClass =
                      entry.amount > 0
                        ? styles.amountPositive
                        : entry.amount < 0
                          ? styles.amountNegative
                          : styles.amountZero;
                    return (
                      <tr key={entry.id}>
                        <td className={styles.numericCell}>{formatDateTime(entry.created_at)}</td>
                        <td>
                          <span className={cn(styles.entryTypeBadge, entryBadgeClass(entry.entry_type))}>
                            {entryBadgeLabel(entry.entry_type)}
                          </span>
                        </td>
                        <td className={cn(styles.numericCell, amountClass)}>
                          {entry.amount > 0 ? `+${formatNumberIntl(entry.amount)}` : formatNumberIntl(entry.amount)}
                        </td>
                        <td className={styles.numericCell}>{formatNumberIntl(entry.balance_after)}</td>
                        <td>{ledgerEntryLabel(entry)}</td>
                        <td className={cn(styles.numericCell, exp.className)}>{exp.label}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </AdminSettingsSection>
      </PageBody>
    </div>
  );
};

export default AiUsage;
